import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { type EmbeddedWebSdk, Fourthline } from '#providers/components/fourthline';
import type { Locale } from '#providers/utils/locale';
import { useReadinessSpan } from '@repo/shared-kit/hooks/use-readiness-span';
import { CFT } from '@repo/shared-kit/sentry/sentry-tags';
import { useSentry } from '@repo/shared-kit/sentry/context';
import { KycProcessStatus, KycProvider, kycQueries } from '@repo/api-kit/queries/due-diligence/kyc';
import { useKycContext } from '../context/kyc-context';
import { useKycProviderDetails } from '../context/provider-details-context';
import { useRefetchProviderDetails } from '#hooks/use-refetch-provider-details';
import { useKycTracking } from '#tracking/use-kyc-tracking';
import { KycError } from '#error/error';
import { getFourthlineProviderDetails } from '#utils/get-fourthline-provider-details';
import { isKycExpired } from '#utils/is-kyc-expired';

export function FourthlineProvider() {
  const { locale } = useIntl();
  const sentry = useSentry();

  const { legalCountry, trackingPrefix, onBack, onComplete, isRegisterFlow } = useKycContext();
  const events = useKycTracking();
  const market = isRegisterFlow ? legalCountry : undefined;
  const {
    data: kycInfo,
    refetch: refetchAcceptance,
    error: kycError,
    isError: isKycError,
  } = useSuspenseQuery(kycQueries.item({ market }));
  const refetch = useRefetchProviderDetails(refetchAcceptance);
  const cancelled = useRef(false);

  const contextProviderDetails = useKycProviderDetails();
  const providerDetails = getFourthlineProviderDetails(kycInfo, contextProviderDetails);

  const token = providerDetails?.token ?? '';
  const redirectUrl = providerDetails?.redirectUrl ?? '';
  const isProviderDetailsExpected = contextProviderDetails.provider === KycProvider.fourthline;
  const isLoading = isProviderDetailsExpected && !providerDetails;
  const processStatus = kycInfo.activeProcess?.processStatus;

  useReadinessSpan('kyc.fourthline', {
    isLoading,
    isError: !token || !redirectUrl || isKycError,
    error: (kycError ?? !token) ? new Error('Missing Fourthline credentials') : undefined,
  });

  useEffect(() => {
    return () => {
      cancelled.current = true;
    };
  }, []);

  useEffect(() => {
    if (processStatus === KycProcessStatus.submitted && !cancelled.current) {
      onComplete(KycProvider.fourthline);
    }
  }, [processStatus, onComplete]);

  const handleOnboardingStarted = useCallback(() => {
    events.fourthlineVerificationScreenLoaded();
  }, [events]);

  const handleOnboardingCompleted = useCallback(() => {
    cancelled.current = true;

    onComplete(KycProvider.fourthline);
  }, [onComplete]);

  const handleOnboardingCompletedError = useCallback(() => {
    sentry.captureMessage('Fourthline onboarding completed with error', {
      level: 'error',
      tags: { component: 'FourthlineProvider', provider: KycProvider.fourthline },
      cft: CFT.IDENTITY_VERIFICATION,
      extra: {
        kycCurrentStatus: kycInfo.kycCurrentStatus,
        processStatus,
        hasProviderDetails: Boolean(providerDetails),
      },
    });
  }, [sentry, kycInfo, processStatus, providerDetails]);

  const handleOnboardingRestartRequest = useCallback(
    async (webSdkElement: { setToken: (token: string) => void }) => {
      events.fourthlineRegenerateLinkClicked();

      const { data } = await refetch();
      const refreshedToken = data?.activeProcess?.providerDetails?.value.token;

      if (refreshedToken) {
        webSdkElement.setToken(refreshedToken);
      }
    },
    [events, refetch]
  );

  const embeddedWebSdkProps = useMemo(
    () =>
      ({
        // oxlint-disable-next-line typescript/consistent-type-assertions
        locale: locale as Locale,
        token,
        events: {
          desktop: {
            onOnboardingStarted: handleOnboardingStarted,
            onOnboardingCompleted: handleOnboardingCompleted,
            onOnboardingCompletedError: handleOnboardingCompletedError,
            onOnboardingRestartRequest: handleOnboardingRestartRequest,
          },
        },
      }) satisfies EmbeddedWebSdk,
    [
      locale,
      token,
      handleOnboardingStarted,
      handleOnboardingCompleted,
      handleOnboardingCompletedError,
      handleOnboardingRestartRequest,
    ]
  );

  if (processStatus === KycProcessStatus.submitted) return null;

  if (!token || !redirectUrl) {
    sentry.captureException(new Error('Fourthline KYC credentials missing'), {
      extra: {
        kycCurrentStatus: kycInfo.kycCurrentStatus,
        processStatus: kycInfo.activeProcess?.processStatus,
        hasProviderDetails: Boolean(providerDetails),
      },
      tags: {
        component: 'FourthlineProvider',
        provider: KycProvider.fourthline,
      },
      cft: CFT.IDENTITY_VERIFICATION,
    });

    return <KycError onRetry={refetch} />;
  }

  const isExpired = isKycExpired(kycInfo);
  const isQesRequired = Boolean(kycInfo.activeProcess?.signatureRequired);

  return (
    <Fourthline
      embeddedWebSdkProps={embeddedWebSdkProps}
      fourthlineRedirectUrl={redirectUrl}
      isKycExpired={isExpired}
      isKycRequired
      isQesRequired={isQesRequired}
      legalCountry={legalCountry}
      mode={trackingPrefix}
      onBack={onBack}
    />
  );
}
